import React, { useState, useMemo } from 'react';
import { PlayIcon, LinkIcon } from '@heroicons/react/24/outline';

const VideoBlock = ({ block, isActive, onChange }) => {
  const [urlInput, setUrlInput] = useState('');
  const { url = '', aspectRatio = '16:9' } = block.content || {};

  // Parse video URL to get embed URL
  const embedUrl = useMemo(() => {
    if (!url) return null;

    try {
      const trimmedUrl = url.trim();

      // YouTube
      if (trimmedUrl.includes('youtube.com') || trimmedUrl.includes('youtu.be')) {
        let videoId = null;

        if (trimmedUrl.includes('youtu.be')) {
          videoId = trimmedUrl.split('youtu.be/')[1]?.split('?')[0]?.split('/')[0];
        } else if (trimmedUrl.includes('youtube.com')) {
          const urlObj = new URL(trimmedUrl);
          videoId = urlObj.searchParams.get('v');
        }

        return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
      }

      // Vimeo
      if (trimmedUrl.includes('vimeo.com')) {
        const videoId = trimmedUrl.split('vimeo.com/')[1]?.split('?')[0]?.split('/')[0];
        return videoId ? `https://player.vimeo.com/video/${videoId}` : null;
      }

      return null;
    } catch (error) {
      return null;
    }
  }, [url]);

  const handleUrlSubmit = () => {
    if (urlInput.trim()) {
      onChange({ url: urlInput.trim() });
      setUrlInput('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleUrlSubmit();
    }
  };

  const aspectRatioClass = {
    '16:9': 'aspect-video',
    '4:3': 'aspect-[4/3]',
    '1:1': 'aspect-square'
  }[aspectRatio] || 'aspect-video';

  if (!url || !embedUrl) {
    return (
      <div className="video-block border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
        <PlayIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
        <p className="text-gray-500 mb-4">Add a video from YouTube or Vimeo</p>

        <div className="max-w-md mx-auto">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="url"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Paste YouTube or Vimeo URL"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleUrlSubmit}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Add
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-400">
            Supported: youtube.com, youtu.be, vimeo.com
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="video-block">
      <div className={`relative ${aspectRatioClass} bg-gray-100 rounded-lg overflow-hidden`}>
        <iframe
          src={embedUrl}
          title="Video"
          className="absolute inset-0 w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>

      {/* Video controls */}
      <div className="mt-3 flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <span>Aspect Ratio:</span>
          <select
            value={aspectRatio}
            onChange={(e) => onChange({ aspectRatio: e.target.value })}
            className="px-2 py-1 border border-gray-200 rounded focus:ring-2 focus:ring-primary-500"
          >
            <option value="16:9">16:9 (Widescreen)</option>
            <option value="4:3">4:3 (Standard)</option>
            <option value="1:1">1:1 (Square)</option>
          </select>
        </label>

        <button
          onClick={() => onChange({ url: '' })}
          className="text-sm text-red-600 hover:text-red-700"
        >
          Remove video
        </button>
      </div>
    </div>
  );
};

export default VideoBlock;
